/**
 * FILE: src/blueprint/api/clientApi.js
 * GOAL: Centralize HTTP calls for the blueprint runner through the client WordPress REST proxy.
 * FLOW:
 * - Reads WP REST root and nonce from the runner mount element dataset or from window.wpApiSettings.
 * - Calls WP config endpoint (same-origin + nonce) to obtain server base URL and flags.
 * - Calls pack discovery and pack/module document endpoints via WP client proxy (same-origin).
 * - Calls run endpoints via WP client proxy (same-origin + nonce), where PHP signs and forwards requests to Savora Server.
 * SECURITY ATTENTION POINTS:
 * - Browser requests should stay same-origin.
 * - The browser should not call the remote Savora Server directly.
 * - The client REST proxy is responsible for server-to-server forwarding and HMAC signing where needed.
 * - Avoid leaking sensitive error details in production.
 * REVIEWED: 2026-05-13
 *
 * @author GoViral Web
 */

function getRunnerMountEl() {
  return document.querySelector('.digivibe-blueprint-runner-root[data-blueprint-runner="1"]');
}

function stripTrailingSlashes(s) {
  return (s || '').replace(/\/+$/, '');
}

function getWpApiRoot() {
  const el = getRunnerMountEl();
  const injected = el?.dataset?.wpApiRoot;
  if (typeof injected === 'string' && injected) return stripTrailingSlashes(injected);

  const root = window?.wpApiSettings?.root;
  if (typeof root === 'string' && root) return stripTrailingSlashes(root);

  return `${window.location.origin}/wp-json`;
}

function getWpNonce() {
  const el = getRunnerMountEl();
  const injected = el?.dataset?.wpNonce;
  if (typeof injected === 'string' && injected) return injected;

  const nonce = window?.wpApiSettings?.nonce;
  return typeof nonce === 'string' && nonce ? nonce : '';
}

function newRequestId() {
  if (typeof crypto !== 'undefined' && crypto?.randomUUID) {
    return crypto.randomUUID();
  }

  return `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function getUnixTimestamp() {
  return Math.floor(Date.now() / 1000);
}

function normalizeProgressivePayload(payload) {
  const source = payload && typeof payload === 'object' ? payload : {};

  return {
    ...source,
    event_type:
      typeof source.event_type === 'string' && source.event_type.trim() !== ''
        ? source.event_type.trim()
        : 'question.answered',
    event_payload:
      source.event_payload &&
      typeof source.event_payload === 'object' &&
      !Array.isArray(source.event_payload)
        ? source.event_payload
        : {},
    timestamp:
      Number.isFinite(source.timestamp) && source.timestamp > 0
        ? Math.floor(source.timestamp)
        : getUnixTimestamp(),
  };
}

async function fetchJson(
  url,
  { method = 'GET', body, signal, credentials = 'same-origin', sendWpNonce = true, requestId } = {}
) {
  const nonce = sendWpNonce ? getWpNonce() : '';
  const rid = requestId || newRequestId();

  const res = await fetch(url, {
    method,
    signal,
    credentials,
    headers: {
      Accept: 'application/json',
      ...(nonce ? { 'X-WP-Nonce': nonce } : {}),
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      'X-Savora-Request-Id': rid,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `${method} ${url} failed (${res.status}) [rid:${rid}]: ${text || res.statusText}`
    );
  }

  return res.json();
}

/**
 * Gets config from client WP.
 * Endpoint: /wp-json/digivibe-savora/v1/config
 */
export async function fetchClientConfig({ signal } = {}) {
  const url = `${getWpApiRoot()}/digivibe-savora/v1/config`;

  return fetchJson(url, {
    method: 'GET',
    signal,
    credentials: 'same-origin',
    sendWpNonce: true,
  });
}

/**
 * Gets the server base URL from config payload.
 * Kept for compatibility with existing callers.
 */
export function getServerBaseUrlFromConfig(config) {
  const base = config?.server_base_url;
  if (typeof base !== 'string') return '';

  return stripTrailingSlashes(base);
}

/**
 * Pack discovery via client proxy.
 * Endpoint: /wp-json/digivibe-savora/v1/packs
 */
export async function fetchPacks({ signal } = {}) {
  const url = `${getWpApiRoot()}/digivibe-savora/v1/packs`;

  return fetchJson(url, {
    method: 'GET',
    signal,
    credentials: 'same-origin',
    sendWpNonce: false,
  });
}

/**
 * Pack blueprint via client proxy.
 * Endpoint: /wp-json/digivibe-savora/v1/packs/{packId}/blueprint
 */
export async function fetchPackBlueprint({ packId, signal } = {}) {
  const url = `${getWpApiRoot()}/digivibe-savora/v1/packs/${encodeURIComponent(packId)}/blueprint`;

  return fetchJson(url, {
    method: 'GET',
    signal,
    credentials: 'same-origin',
    sendWpNonce: false,
  });
}

/**
 * Pack modules overview via client proxy.
 * Endpoint: /wp-json/digivibe-savora/v1/packs/{packId}/modules
 */
export async function fetchPackModulesOverview({ packId, signal } = {}) {
  const url = `${getWpApiRoot()}/digivibe-savora/v1/packs/${encodeURIComponent(packId)}/modules`;

  return fetchJson(url, {
    method: 'GET',
    signal,
    credentials: 'same-origin',
    sendWpNonce: false,
  });
}

/**
 * Module document via client proxy.
 * Endpoint: /wp-json/digivibe-savora/v1/packs/{packId}/modules/{moduleId}?track={trackId}
 */
export async function fetchModuleDoc({ packId, moduleId, trackId, signal } = {}) {
  const url =
    `${getWpApiRoot()}/digivibe-savora/v1/packs/${encodeURIComponent(packId)}` +
    `/modules/${encodeURIComponent(moduleId)}?track=${encodeURIComponent(trackId)}`;

  return fetchJson(url, {
    method: 'GET',
    signal,
    credentials: 'same-origin',
    sendWpNonce: false,
  });
}

export async function fetchBlueprintRunOverview({ payload, signal, requestId } = {}) {
  const url = `${getWpApiRoot()}/digivibe-savora/v1/savora/overview-state`;

  return fetchJson(url, {
    method: 'POST',
    body: payload,
    signal,
    credentials: 'same-origin',
    sendWpNonce: true,
    requestId,
  });
}

/**
 * Posts progressive run payload to client WP proxy.
 */
export async function postRunProgressive({ payload, signal, requestId } = {}) {
  const url = `${getWpApiRoot()}/digivibe-savora/v1/savora/progressive`;

  return fetchJson(url, {
    method: 'POST',
    body: normalizeProgressivePayload(payload),
    signal,
    credentials: 'same-origin',
    sendWpNonce: true,
    requestId,
  });
}

export async function postResetModule({ payload, signal, requestId } = {}) {
  const url = `${getWpApiRoot()}/digivibe-savora/v1/savora/reset-module`;

  return fetchJson(url, {
    method: 'POST',
    body: payload,
    signal,
    credentials: 'same-origin',
    sendWpNonce: true,
    requestId,
  });
}

export async function postRunCommit({ payload, signal, requestId } = {}) {
  const url = `${getWpApiRoot()}/digivibe-savora/v1/savora/commit`;

  return fetchJson(url, {
    method: 'POST',
    body: payload,
    signal,
    credentials: 'same-origin',
    sendWpNonce: true,
    requestId,
  });
}

export async function postCoachInteraction({ payload, signal, requestId } = {}) {
  const url = `${getWpApiRoot()}/digivibe-savora/v1/savora/coach/interact`;

  return fetchJson(url, {
    method: 'POST',
    body: payload,
    signal,
    credentials: 'same-origin',
    sendWpNonce: true,
    requestId,
  });
}
